import React, { useState, useEffect } from 'react';
import { X, Plus, Sparkles, Loader2, Check, Dumbbell } from 'lucide-react';
import MuscleIcon from './MuscleIcons';
import { getAIExerciseAlternatives } from '../services/aiService';

const MUSCLE_CATEGORIES = [
  'Épaules', 'Biceps', 'Triceps', 'Avant-bras', 'Dos', 'Pectoraux', 
  'Abdos', 'Quadriceps', 'Ischio-jambiers', 'Mollets', 'Cou', 'Fessier', 'Callisthénie', 'Cardio'
];

const getMuscles = (ex) => {
  let rawMuscles = ex.muscles || (ex.muscle ? [ex.muscle] : null);
  if (!rawMuscles) return ['Autre'];
  const arr = Array.isArray(rawMuscles) ? rawMuscles : [rawMuscles];
  return arr.map(m => {
    if (m === 'Jambes') {
      const lowerName = (ex.name || '').toLowerCase();
      if (/(curl|ischio|soulevé|souleve|deadlift|sdt|good morning|rdl|arrière cuisse|arriere cuisse)/.test(lowerName)) return 'Ischio-jambiers';
      if (/(hip thrust|fessier|glute|abduct)/.test(lowerName)) return 'Fessier';
      return 'Quadriceps';
    }
    return m;
  });
};

const ExerciseImage = ({ exercise }) => {
  const [frame, setFrame] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (exercise.animationImages && exercise.animationImages.length > 1) {
      const interval = setInterval(() => {
        setFrame(prev => (prev + 1) % exercise.animationImages.length);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [exercise]);

  useEffect(() => {
    setLoading(true);
    setError(false);
  }, [exercise]);

  const hasMedia = Boolean(
    (exercise.animationImages && exercise.animationImages.length > 0) || 
    exercise.gifUrl || 
    exercise.videoUrl
  );

  if (hasMedia && !error) {
    let src = '';
    if (exercise.animationImages && exercise.animationImages.length > 0) {
      src = exercise.animationImages[frame];
    } else {
      src = exercise.gifUrl || exercise.videoUrl;
    }
    
    return (
      <div style={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {loading && (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.65rem',
            backgroundColor: '#fff',
            zIndex: 2
          }}>
            <Loader2 size={36} color="var(--primary-color)" style={{ animation: 'spin 1s linear infinite' }} />
            <span style={{ fontSize: '0.82rem', color: '#666', fontWeight: 500 }}>
              Chargement du visuel...
            </span>
          </div>
        )}
        <img 
          src={src} 
          alt={exercise.name} 
          style={{ 
            width: '100%', 
            height: '100%', 
            objectFit: 'contain', 
            backgroundColor: '#fff', 
            opacity: loading ? 0 : 1,
            transition: 'opacity 0.25s ease'
          }} 
          onLoad={() => setLoading(false)}
          onError={() => { setLoading(false); setError(true); }}
        />
      </div>
    );
  }

  const muscles = getMuscles(exercise);
  const primaryMuscle = muscles[0] && muscles[0] !== 'Autre' ? muscles[0] : null;

  return (
    <div style={{
      width: '100%',
      height: '100%',
      background: 'linear-gradient(145deg, rgba(140, 127, 191, 0.1) 0%, rgba(110, 181, 131, 0.06) 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1.5rem 1rem',
      textAlign: 'center',
      gap: '0.65rem'
    }}>
      <div style={{
        width: '74px',
        height: '74px',
        borderRadius: '50%',
        background: 'rgba(140, 127, 191, 0.15)',
        border: '2px solid rgba(140, 127, 191, 0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 6px 16px rgba(0, 0, 0, 0.15)',
        flexShrink: 0
      }}>
        {primaryMuscle ? (
          <MuscleIcon type={primaryMuscle} size={48} />
        ) : (
          <Dumbbell size={36} color="var(--primary-color)" />
        )}
      </div>

      <div>
        <div style={{ fontWeight: 600, fontSize: '0.95rem', color: 'var(--text-color)' }}>
          Aucune démonstration disponible
        </div>
      </div>

      <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'center', marginTop: '0.2rem' }}>
        {primaryMuscle && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: '0.72rem',
            fontWeight: 600,
            color: 'var(--primary-color)',
            backgroundColor: 'rgba(140, 127, 191, 0.18)',
            padding: '0.2rem 0.55rem',
            borderRadius: '12px',
            border: '1px solid rgba(140, 127, 191, 0.3)'
          }}>
            💪 {primaryMuscle}
          </span>
        )}
        {exercise.equipment && exercise.equipment !== 'Autre' && (
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            fontSize: '0.72rem',
            fontWeight: 600,
            color: 'var(--secondary-color)',
            backgroundColor: 'rgba(110, 181, 131, 0.18)',
            padding: '0.2rem 0.55rem',
            borderRadius: '12px',
            border: '1px solid rgba(110, 181, 131, 0.3)'
          }}>
            🏋️ {exercise.equipment}
          </span>
        )}
      </div>
    </div>
  );
};

export const ExercisePreviewModal = ({ exercise, onClose, onAdd, isAlreadyAdded = false, zIndex = 10005 }) => {
  if (!exercise) return null;

  const hasMedia = Boolean(
    (exercise.animationImages && exercise.animationImages.length > 0) || 
    exercise.gifUrl || 
    exercise.videoUrl
  );

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.85)', zIndex: zIndex,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      backdropFilter: 'blur(3px)'
    }} onClick={onClose}>
      <div 
        className="card" 
        style={{ width: '100%', maxWidth: '400px', padding: '1.5rem', position: 'relative' }} 
        onClick={e => e.stopPropagation()}
      >
        <button className="btn-icon" onClick={onClose} style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
          <X size={24} />
        </button>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', paddingRight: '2rem', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0 }}>
            {exercise.name}
            {exercise.equipment && exercise.equipment !== 'Autre' && (
              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                ({exercise.equipment})
              </span>
            )}
          </h3>
          {isAlreadyAdded && (
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.3rem',
              backgroundColor: 'rgba(110, 181, 131, 0.15)',
              color: 'var(--secondary-color)',
              padding: '0.2rem 0.6rem',
              borderRadius: '20px',
              fontSize: '0.75rem',
              fontWeight: 600,
              border: '1px solid rgba(110, 181, 131, 0.3)'
            }}>
              <Check size={13} /> Déjà dans le programme
            </span>
          )}
        </div>
        
        <div style={{
          width: '100%',
          height: '250px',
          backgroundColor: hasMedia ? '#fff' : 'transparent',
          borderRadius: 'var(--border-radius)',
          overflow: 'hidden',
          marginBottom: '1.5rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          border: hasMedia ? '1px solid var(--border-color)' : '1px dashed rgba(140, 127, 191, 0.35)',
          position: 'relative'
        }}>
          <ExerciseImage exercise={exercise} />
        </div>
        
        <div style={{ marginBottom: '1.5rem', fontSize: '0.9rem' }}>
          <strong>Muscles:</strong> {getMuscles(exercise).join(', ')}
        </div>

        {onAdd && (
          <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => onAdd(exercise)}>
            {isAlreadyAdded ? "Ajouter à nouveau" : "Ajouter au programme"}
          </button>
        )}
      </div>
    </div>
  );
};

export const ExerciseSelectorModal = ({ exercises, onClose, onSelect, replaceTargetExercise, selectedExercises = [] }) => {
  const [muscleFilter, setMuscleFilter] = useState([]);
  const [previewEx, setPreviewEx] = useState(null);
  
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);

  const isExerciseSelected = (ex) => {
    if (!ex || !selectedExercises || selectedExercises.length === 0) return false;
    return selectedExercises.some(block => {
      if (block.type === 'superset' && Array.isArray(block.items)) {
        return block.items.some(item => 
          (item.exerciseId && item.exerciseId === ex.id) || 
          (item.id === ex.id) || 
          (item.name && ex.name && item.name.toLowerCase() === ex.name.toLowerCase() && (item.equipment || '') === (ex.equipment || ''))
        );
      }
      return (
        (block.exerciseId && block.exerciseId === ex.id) ||
        (block.id === ex.id) ||
        (block.name && ex.name && block.name.toLowerCase() === ex.name.toLowerCase() && (block.equipment || '') === (ex.equipment || ''))
      );
    });
  };

  useEffect(() => {
    let isMounted = true;
    const fetchSuggestions = async () => {
      if (replaceTargetExercise && exercises.length > 0) {
        setAiLoading(true);
        const suggestedIds = await getAIExerciseAlternatives(replaceTargetExercise, exercises);
        if (isMounted) {
          const suggestions = suggestedIds.map(id => exercises.find(ex => ex.id === id)).filter(Boolean);
          setAiSuggestions(suggestions);
          setAiLoading(false);
        }
      }
    };
    fetchSuggestions();
    return () => { isMounted = false; };
  }, [replaceTargetExercise, exercises]);

  const filteredExercises = muscleFilter.length > 0 
    ? exercises.filter(ex => getMuscles(ex).some(m => muscleFilter.includes(m))) 
    : exercises;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem'
    }} onClick={onClose}>
      <div 
        className="card" 
        style={{ width: '100%', maxWidth: '600px', height: '80vh', padding: '1rem', display: 'flex', flexDirection: 'column' }} 
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-3">
          <h3 style={{ margin: 0 }}>Sélectionner un exercice</h3>
          <button className="btn-icon" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
          {Array.from(new Set([
            ...MUSCLE_CATEGORIES,
            ...exercises.flatMap(e => getMuscles(e))
          ]))
            .filter(cat => cat !== 'Autre' && cat !== 'Bras' && cat !== 'Dos inférieur' && cat !== 'Triceps sural' && cat !== 'Abdominaux' && cat !== 'Jambes')
            .map(cat => {
            const isActive = muscleFilter.includes(cat);
            return (
              <button
                key={cat}
                type="button"
                onClick={() => {
                  if (isActive) setMuscleFilter(muscleFilter.filter(m => m !== cat));
                  else setMuscleFilter([...muscleFilter, cat]);
                }}
                style={{
                  padding: '0.4rem 0.8rem',
                  borderRadius: '20px',
                  border: `1px solid ${isActive ? 'var(--primary-color)' : 'var(--border-color)'}`,
                  backgroundColor: isActive ? 'var(--primary-color)' : 'var(--bg-color)',
                  color: isActive ? '#fff' : 'var(--text-secondary)',
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  fontWeight: isActive ? 'bold' : 'normal'
                }}
              >
                {cat}
              </button>
            );
          })}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--border-radius-sm)', padding: '0.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          
          {replaceTargetExercise && (
            <div style={{ padding: '1rem', backgroundColor: 'rgba(162, 155, 254, 0.1)', borderRadius: 'var(--border-radius)', border: '1px solid rgba(162, 155, 254, 0.3)' }}>
              <h4 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#a29bfe', marginBottom: '1rem', marginTop: 0 }}>
                <Sparkles size={18} /> Suggestions du Coach IA
              </h4>
              
              {aiLoading ? (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.9rem', padding: '1rem 0' }}>
                  <div className="spinner" style={{ margin: '0 auto 0.5rem auto' }}></div>
                  L'IA réfléchit aux meilleures alternatives...
                </div>
              ) : aiSuggestions.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {aiSuggestions.map(ex => {
                    const isAdded = isExerciseSelected(ex);
                    return (
                      <div 
                        key={'ai-' + ex.id} 
                        style={{ 
                          padding: '0.8rem', 
                          backgroundColor: isAdded ? 'rgba(110, 181, 131, 0.08)' : 'var(--bg-color)',
                          borderRadius: 'var(--border-radius-sm)',
                          border: isAdded ? '1px solid rgba(110, 181, 131, 0.3)' : '1px solid var(--border-color)', 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          cursor: 'pointer'
                        }}
                        className="hover:bg-border-color"
                        onClick={() => setPreviewEx(ex)}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: isAdded ? '600' : '500' }}>
                            {ex.name}
                            {ex.equipment && ex.equipment !== 'Autre' && (
                              <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                                ({ex.equipment})
                              </span>
                            )}
                          </span>
                          {isAdded && (
                            <span style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.25rem',
                              fontSize: '0.72rem',
                              fontWeight: '600',
                              color: 'var(--secondary-color)',
                              backgroundColor: 'rgba(110, 181, 131, 0.15)',
                              padding: '0.15rem 0.5rem',
                              borderRadius: '12px',
                              border: '1px solid rgba(110, 181, 131, 0.3)'
                            }}>
                              <Check size={12} /> Ajouté
                            </span>
                          )}
                        </div>
                        <button 
                          type="button" 
                          className="btn-icon" 
                          onClick={(e) => { e.stopPropagation(); onSelect(ex); onClose(); }}
                          title={isAdded ? "Ajouter à nouveau" : "Ajouter"}
                        >
                          <Plus size={20} color="var(--primary-color)" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', textAlign: 'center' }}>
                  Aucune suggestion IA trouvée. Voici la liste complète.
                </div>
              )}
            </div>
          )}

          <div>
            {replaceTargetExercise && <h5 style={{ color: 'var(--text-secondary)', marginBottom: '0.5rem', marginLeft: '0.5rem' }}>Tous les exercices</h5>}
            {filteredExercises.map(ex => {
              const isAdded = isExerciseSelected(ex);
              return (
                <div 
                  key={ex.id} 
                  style={{ 
                    padding: '0.8rem', 
                    borderBottom: '1px solid var(--border-color)', 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    backgroundColor: isAdded ? 'rgba(110, 181, 131, 0.05)' : 'transparent'
                  }}
                  className="hover:bg-border-color"
                  onClick={() => setPreviewEx(ex)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: isAdded ? '600' : '400' }}>
                      {ex.name}
                      {ex.equipment && ex.equipment !== 'Autre' && (
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 'normal', marginLeft: '0.5rem' }}>
                          ({ex.equipment})
                        </span>
                      )}
                    </span>
                    {isAdded && (
                      <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontSize: '0.72rem',
                        fontWeight: '600',
                        color: 'var(--secondary-color)',
                        backgroundColor: 'rgba(110, 181, 131, 0.15)',
                        padding: '0.15rem 0.5rem',
                        borderRadius: '12px',
                        border: '1px solid rgba(110, 181, 131, 0.3)'
                      }}>
                        <Check size={12} /> Ajouté
                      </span>
                    )}
                  </div>
                  <button 
                    type="button" 
                    className="btn-icon" 
                    onClick={(e) => { e.stopPropagation(); onSelect(ex); onClose(); }}
                    title={isAdded ? "Ajouter à nouveau" : "Ajouter"}
                  >
                    <Plus size={20} color="var(--primary-color)" />
                  </button>
                </div>
              );
            })}
            {filteredExercises.length === 0 && (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                Aucun exercice trouvé.
              </div>
            )}
          </div>
        </div>
      </div>

      {previewEx && (
        <ExercisePreviewModal 
          exercise={previewEx} 
          isAlreadyAdded={isExerciseSelected(previewEx)}
          onClose={() => setPreviewEx(null)} 
          onAdd={(ex) => { onSelect(ex); setPreviewEx(null); onClose(); }} 
        />
      )}
    </div>
  );
};
